import { useLazyQuery, useQuery } from '@apollo/client';
import useQueryHelper, {
    ORDER_BY,
    ORDER_DIR,
    QUERY_FETCH_POLICY,
    QUERY_NO_CACHE_FETCH_POLICY,
    QueryStatusData,
} from '../../../../core/hooks/useQueryHelper';
import useQueryErrorHandler from '../../../../core/hooks/useQueryErrorHandler';
import { ServiceRequest } from '../../../types/serviceRequest';
import PAGE_CONTEXT from '../../../../core/datasets/page_context';
import { useDispatch } from 'react-redux';
import { FILTER_GROUP } from '../../../../core/datasets/filter';
import { getDefaultValuesForGroup } from '../../../../core/utils/useFiltersHelper';
import { setServiceRequestFilterGroups } from '../../../../core/redux/actions/filterActions';
import { useState } from 'react';
import { ServiceRequestsDocument } from './gql/serviceRequestDocument';

const useQueryServiceRequest = (): QueryServiceRequestReturnType => {
    const [shouldSkipFilters, setShouldSkipFilters] = useState(false);

    const dispatch = useDispatch();
    const { getFilterValue } = useQueryHelper();
    const filters = getFilterValue(PAGE_CONTEXT.SERVICE_REQUESTS);
    const variables = {
        orderDir: ORDER_DIR.desc,
        orderBy: ORDER_BY.submitDate,
        filterBy: filters,
    };

    const { errorHandler } = useQueryErrorHandler();
    const defaultResponse = { serviceRequestsV2: [] };

    const options = {
        variables: variables,
        ...QUERY_FETCH_POLICY,
        onError: errorHandler,
    };

    const updateServiceRequestFilters = (serviceRequestsList: ServiceRequest[]) => {
        if (shouldSkipFilters) {
            return;
        }

        dispatch(
            setServiceRequestFilterGroups({
                groups: {
                    //@ts-ignore
                    [FILTER_GROUP.STATUS]: getDefaultValuesForGroup(FILTER_GROUP.STATUS, serviceRequestsList),
                    // @ts-ignore
                    [FILTER_GROUP.TYPE_ID]: getDefaultValuesForGroup(FILTER_GROUP.TYPE_ID, serviceRequestsList),
                },
                context: PAGE_CONTEXT.SERVICE_REQUESTS,
            }),
        );
    };

    const {
        loading,
        error,
        data = defaultResponse,
    } = useQuery<QueryResponse>(ServiceRequestsDocument, {
        ...options,
        onCompleted: (response) => {
            if (shouldSkipFilters) {
                return;
            }

            setShouldSkipFilters(true);
            updateServiceRequestFilters(response.serviceRequestsV2);
        },
    });

    const [getServiceRequests, { loading: lazyLoading, error: lazyError, data: lazyData = defaultResponse, called }] =
        useLazyQuery<QueryResponse>(ServiceRequestsDocument, {
            variables: variables,
            ...QUERY_NO_CACHE_FETCH_POLICY,
            onError: errorHandler,
            onCompleted: (response) => {
                if (shouldSkipFilters) {
                    return;
                }

                setShouldSkipFilters(true);
                updateServiceRequestFilters(response.serviceRequestsV2);
            },
        });

    return {
        serviceRequests: data.serviceRequestsV2.map(toServiceRequestWithMeta),
        lazyServiceRequests: lazyData.serviceRequestsV2.map(toServiceRequestWithMeta),
        getServiceRequests,
        loading: lazyLoading || loading,
        error: lazyError || error,
        called,
    };
};

type QueryServiceRequestReturnType = QueryStatusData & {
    serviceRequests: ServiceRequest[];
    getServiceRequests: () => void;
    lazyServiceRequests: ServiceRequest[];
    called: boolean;
};

type QueryResponse = { serviceRequestsV2: ServiceRequest[] };

function toServiceRequestWithMeta(original: ServiceRequest) {
    return {
        ...original,
        meta: {
            id: original.id,
        },
    };
}

export default useQueryServiceRequest;
